import React from "react";
import PropTypes from "prop-types";
import { Container, Box, Grid } from "@mui/material";
import {
  getCrmCustomersGrowth,
  getCrmCustomersNewVsReturning,
  getCrmCustomersLifetimeValue,
  getCrmCustomersRepeatPurchaseRate,
} from "../../api";
import { useAnalyticsFetch } from "./hooks/useAnalyticsFetch";
import LineChartCard from "./components/LineChartCard";
import DoughnutChartCard from "./components/DoughnutChartCard";
import LeaderboardCard from "./components/LeaderboardCard";
import KpiCard from "./components/KpiCard";
import { formatCurrency } from "./components/formatters";

const LTV_COLUMNS = [
  { key: "customer_id", label: "Customer ID" },
  { key: "order_count", label: "Orders" },
  { key: "total_spent", label: "Total Spent", format: formatCurrency },
  { key: "first_order_date", label: "First Order" },
  { key: "last_order_date", label: "Last Order" },
];

const Customers = ({ filter, startDate, endDate }) => {
  const { data: growth, loading: growthLoading } = useAnalyticsFetch(getCrmCustomersGrowth, [
    filter,
    startDate,
    endDate,
    "day",
  ]);

  const { data: newVsReturning, loading: newVsReturningLoading } = useAnalyticsFetch(
    getCrmCustomersNewVsReturning,
    [filter, startDate, endDate],
  );

  const { data: ltv, loading: ltvLoading } = useAnalyticsFetch(getCrmCustomersLifetimeValue, [
    filter,
    startDate,
    endDate,
    null,
    10,
  ]);

  const { data: repeatRate, loading: repeatRateLoading } = useAnalyticsFetch(
    getCrmCustomersRepeatPurchaseRate,
    [filter, startDate, endDate],
  );

  return (
    <Container sx={{ paddingLeft: "0 !important", paddingRight: "0 !important" }}>
      <Grid container spacing={2} sx={{ marginBottom: "1rem" }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Customers (Cohort)" value={ltv?.customer_count ?? 0} color="#2271b1" loading={ltvLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Average LTV" value={ltv?.average_ltv ?? 0} color="#5b841b" currency loading={ltvLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Repeat Purchase Rate"
            value={repeatRate?.repeat_purchase_rate_percent != null ? `${repeatRate.repeat_purchase_rate_percent}%` : "0%"}
            color="#8e44ad"
            loading={repeatRateLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Repeat Customers" value={repeatRate?.repeat_customers ?? 0} color="#16a085" loading={repeatRateLoading} />
        </Grid>
      </Grid>

      <Box sx={{ marginBottom: "1rem" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <LineChartCard
              title="New Customer Growth"
              labels={growth?.growth?.labels || []}
              datasets={growth?.growth ? [{ label: "New Customers", data: growth.growth.series }] : []}
              loading={growthLoading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DoughnutChartCard
              title="New vs Returning"
              labels={newVsReturning?.chart?.labels || []}
              series={newVsReturning?.chart?.series || []}
              loading={newVsReturningLoading}
            />
          </Grid>
        </Grid>
      </Box>

      <LeaderboardCard
        title="Top Customers by Lifetime Value"
        rows={ltv?.top_customers || []}
        columns={LTV_COLUMNS}
        loading={ltvLoading}
      />
    </Container>
  );
};

Customers.propTypes = {
  filter: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

export default Customers;
